import React, { useCallback, useEffect, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useGetKRDetailsQuery } from 'components/RabbitOKRSidePanel/datahooks';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { FeedbackSentiment, VisibilityType } from 'interfaces/CF';
import { Coin } from 'interfaces/MeshMaven';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useHelpMeWriteTooltip from 'screens/common/useHelpMeWriteTooltip';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkCFGenAIEnabled, checkDenyPublicCFVisibilityEnabled } from 'utils/helpers/featureFlags';

import { Plus, RabbitCompetency } from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';

import {
  FEEDBACK_FORM_VISIBILITY_RABBIT,
  FEEDBACK_FORM_VISIBILITY_RABBIT_WITHOUT_PUBLIC,
  SENTIMENTS,
} from 'constants/ProjectConstants';
import { generateKey, getQueryParams } from 'constants/helper';
import { sendClickedOnHelpMeWriteEvent } from 'constants/mixpanelHelper';
import { CF_GEN_AI_BUTTON_TYPES, sendCFGenAIButtonClickedEvent } from 'constants/mixpanelHelpers/cfGenAIEvents';

import { Button } from 'uiToolkitV1/Button';
import MavenButton from 'uiToolkitV1/MavenButton/MavenButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import FeedbackCompetencies from '../common/FeedbackCompetencies';
import FeedbackRTE from '../common/FeedbackRTE';
import FeedbackVisibilityDropdown from '../common/FeedbackVisibilityDropdown';
import { useFeedbackAnalysisMutation } from '../hooks/meshMavenDataHooks';
import {
  setProvidedAddRevision,
  setProvidedApplicableEmployees,
  setProvidedAttachments,
  setProvidedFeedbackPost,
  setProvidedFeedbackSentimentType,
  setProvidedGoal,
  setProvidedRevisionNumber,
  setProvidedVisibility,
  useProvidedFeedbackData,
} from '../slice/feedbackSlice';

export interface ShareFeedbackBodyProps {
  source: string;
  openSecondaryPanel: (panelName: string) => void;
  isSharePublicPraise?: boolean;
}

// eslint-disable-next-line no-shadow
enum FeedbackSentimentType {
  APPRECIATIVE = 'APPRECIATIVE',
  CONSTRUCTIVE = 'CONSTRUCTIVE',
}

export const ShareFeedbackBody = ({ source, openSecondaryPanel, isSharePublicPraise }: ShareFeedbackBodyProps) => {
  const {
    feedbackPost,
    feedbackSentimentType,
    goal = {},
    visibility,
    attachments,
    applicableEmployees,
    revisions,
  } = useProvidedFeedbackData();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const { moduleName } = useOrganisationSettings();

  const helpMeWriteTooltip = useHelpMeWriteTooltip({ editorState: feedbackPost });

  const parentRef = useRef(null);

  const dispatch = useDispatch();
  const location = useLocation();

  const { search } = location ?? {};
  const { feedbackType, goalId } = getQueryParams(search);
  const relatedGoalDetails = useGetKRDetailsQuery({ id: goalId, enabled: !!goalId });

  const isFeedbackVisibilityDropdownDisabled = isSharePublicPraise;

  const { mutate: getFeedbackAnalysis } = useFeedbackAnalysisMutation();

  const onFeedbackTypeSelect = useCallback(
    (type: FeedbackSentiment) => {
      // set the appropriate visibility wrt to the feedback type
      dispatch(
        setProvidedVisibility({
          visibility:
            type === 'APPRECIATIVE' && isSharePublicPraise && !checkDenyPublicCFVisibilityEnabled()
              ? 'PUBLIC'
              : 'RECEIVER_AND_MANAGER',
        })
      );
      dispatch(
        setProvidedFeedbackSentimentType({
          feedbackSentimentType: type,
        })
      );
    },
    [dispatch, isSharePublicPraise]
  );

  useEffect(() => {
    // calculating initial visibility value on mounting based on a flag

    if (isSharePublicPraise === false) {
      onFeedbackTypeSelect(FeedbackSentimentType.CONSTRUCTIVE);
      dispatch(
        setProvidedVisibility({
          visibility: FEEDBACK_FORM_VISIBILITY_RABBIT_WITHOUT_PUBLIC[0].value as VisibilityType,
        })
      );
    } else {
      onFeedbackTypeSelect(FeedbackSentimentType.APPRECIATIVE);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharePublicPraise]);

  const onFeedbackVisibilitySelect = (type) => {
    dispatch(
      setProvidedVisibility({
        visibility: type.value,
      })
    );
  };

  const onUpdateEditorState = (editorState) => {
    dispatch(
      setProvidedFeedbackPost({
        feedbackPost: editorState,
      })
    );
  };

  const setImage = (image) => {
    dispatch(
      setProvidedAttachments({
        images: [
          {
            type: 'IMAGE',
            url: image?.url,
            uuid: generateKey(4),
          },
        ],
      })
    );
  };

  const setGif = (gif) => {
    dispatch(
      setProvidedAttachments({
        gifs: [
          {
            type: 'GIF',
            url: gif,
            uuid: generateKey(4),
          },
        ],
      })
    );
  };

  const onDeleteImage = () => {
    dispatch(
      setProvidedAttachments({
        images: [],
      })
    );
  };

  const setCompetency = (editorState) => {
    dispatch(
      setProvidedFeedbackPost({
        feedbackPost: editorState,
      })
    );
  };

  const handleHelpMeWrite = () => {
    const newRevision = {
      requestId: '',
      originalState: feedbackPost,
      updatedState: feedbackPost,
      isAIFeedbackProvided: false,
      isReanalyzeEnabled: false,
      feedbackAnalysis: { context: 0, observations: 0, impact: 0, next_steps: 0 } as Coin,
    };
    sendCFGenAIButtonClickedEvent({
      eventProperties: {
        source,
        type: CF_GEN_AI_BUTTON_TYPES.HELP_ME_WRITE,
      },
    });
    sendClickedOnHelpMeWriteEvent({
      eventProperties: {
        source: 'Share Feedback',
      },
    });
    openSecondaryPanel('MESH_MAVEN');
    dispatch(
      setProvidedAddRevision({
        revision: newRevision,
      })
    );
    dispatch(setProvidedRevisionNumber({ revisionNumber: revisions.length }));
    getFeedbackAnalysis({
      revision: newRevision,
      revisionNumber: revisions.length,
      editorState: feedbackPost,
    });
  };

  const setApplicableEmployeeIds = (employeeIds) => {
    dispatch(
      setProvidedApplicableEmployees({
        applicableEmployees: employeeIds,
      })
    );
  };

  useEffect(() => {
    if (
      (feedbackType as string) === FeedbackSentimentType.APPRECIATIVE ||
      (feedbackType as string) === FeedbackSentimentType.CONSTRUCTIVE
    ) {
      onFeedbackTypeSelect(feedbackType as FeedbackSentiment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackType, onFeedbackTypeSelect]);

  useEffect(() => {
    if (relatedGoalDetails?.status === 'success' && relatedGoalDetails?.data) {
      dispatch(
        setProvidedGoal({
          goal: { ...relatedGoalDetails?.data, title: relatedGoalDetails?.data?.text },
        })
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedGoalDetails?.status]);

  return (
    <Box w="100%">
      {/* @ts-expect-error: Union too complex */}
      <Flex
        alignItems={{
          base: 'flex-start',
          md: 'center',
        }}
        justifyContent="space-between"
        w="100%"
        p="8px 12px"
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
      >
        {isSharePublicPraise ? (
          <Flex alignItems="center" className="feedback-visibility">
            <TextLabel mr="8px" color="text.g2">
              Visibility:
            </TextLabel>
            <TextBodyMedium>
              {FEEDBACK_FORM_VISIBILITY_RABBIT.find((option) => option.value === visibility).label}
            </TextBodyMedium>
          </Flex>
        ) : (
          <FeedbackVisibilityDropdown
            onChange={onFeedbackVisibilitySelect}
            selectedValue={visibility}
            isSharePublicPraise={isSharePublicPraise}
            isDisabled={isFeedbackVisibilityDropdownDisabled}
          />
        )}
      </Flex>

      <Box ref={parentRef} mt="8px" pos="relative">
        {visibility === 'PUBLIC' && (
          <TextLabel bold color="#FD812D" p="0px 12px" mb="4px">
            This feedback is public and will be visible to everyone in the organisation
          </TextLabel>
        )}
        {/* @ts-expect-error missing the following properties from type 'Props': onEnter, onEscape */}
        <FeedbackRTE
          placeholder="Type your feedback here (Use @ to tag people and # to include competencies)"
          parentRef={parentRef}
          editorState={feedbackPost}
          updateEditorState={onUpdateEditorState}
          setImage={setImage}
          setGif={setGif}
          uploadedImage={attachments?.images?.length ? attachments?.images[0] : null}
          uploadedGif={attachments?.gifs?.length ? attachments?.gifs[0] : null}
          onDeleteImage={onDeleteImage}
          mentionExclusions={feedbackSentimentType === SENTIMENTS.appreciative ? [loggedInEmployee.id] : []}
        />
        {checkCFGenAIEnabled() && (
          <Box pos="absolute" right={2} bottom={0}>
            <Tooltip placement="bottom-end" label={helpMeWriteTooltip.tooltip}>
              <MavenButton
                // @ts-expect-error not assignable to type 'IntrinsicAttributes & RefAttributes<any>'
                text="Help me write"
                isDisabled={helpMeWriteTooltip.isDisabled}
                onClick={handleHelpMeWrite}
              />
            </Tooltip>
          </Box>
        )}
      </Box>
      <Flex p="8px" flexDir="column" bg="#FBFCFD" borderRadius="8px">
        <Flex p="4px" alignItems="center">
          <RabbitCompetency boxSize="20px" fill="transparent" />
          <TextLabel bold>Competency</TextLabel>
        </Flex>
        <FeedbackCompetencies
          editorState={feedbackPost}
          setApplicableEmployeeIds={setApplicableEmployeeIds}
          setCompetency={setCompetency}
        />
      </Flex>
      <Flex alignItems="start" p="8px" flexDir="column" mt="12px">
        {/* @ts-expect-error Property 'uuid' does not exist on type '{}' */}
        {!goal?.uuid ? (
          <Tooltip
            fontSize="14px"
            label={
              applicableEmployees?.length === 0
                ? `You need to tag people to add related ${moduleName}. Use @ to tag people`
                : ''
            }
          >
            <Box as="span">
              <Button
                variant="tertiary"
                StartIcon={Plus}
                onClick={applicableEmployees?.length === 0 ? () => {} : () => openSecondaryPanel('GOAL_SELECTION')}
                cursor={applicableEmployees?.length === 0 ? 'not-allowed !important' : 'pointer !important'}
                opacity={applicableEmployees?.length === 0 ? 0.6 : 1}
              >
                Add Related {moduleName}
              </Button>
            </Box>
          </Tooltip>
        ) : (
          <Box p="8px 12px ">
            <TextLabel mb="4px" color="text.g2">
              Related {moduleName}
            </TextLabel>
            <Flex alignItems="center" cursor="pointer" onClick={() => openSecondaryPanel('GOAL_SELECTION')}>
              <KeyResult color="#7651BC" boxSize="18px" mr="8px" />
              {/* @ts-expect-error Property 'title' does not exist on type '{}' */}
              <TextBodyMedium>{goal?.title}</TextBodyMedium>
            </Flex>
          </Box>
        )}
      </Flex>
    </Box>
  );
};
